import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../../config/api';
import { Search, Ban, RotateCcw, CheckCircle, Users } from 'lucide-react';

const API = `${API_BASE}/api`;
const STATUS = { active: 'bg-green-500/15 text-green-400', revoked: 'bg-red-500/15 text-red-400' };
const inputCls = 'h-9 bg-navigate-surface border border-zinc-800 rounded-md text-sm text-white px-2';

export default function ProgrammeEnrollmentsAdmin({ headers, initialProgramme = '' }) {
  const [view, setView] = useState('list');
  const [programmes, setProgrammes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [progFilter, setProgFilter] = useState(initialProgramme);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    axios.get(`${API}/programmes/admin/all`, { headers })
      .then((r) => setProgrammes(Array.isArray(r.data) ? r.data : (r.data.programmes || [])))
      .catch(() => toast.error('Failed to load programmes'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (progFilter) params.append('programme_id', progFilter);
      if (statusFilter) params.append('status', statusFilter);
      const res = await axios.get(`${API}/admin/programme-enrollments?${params}`, { headers });
      setRows(res.data.enrollments || []);
    } catch (e) { toast.error('Failed to load programme enrolments'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, progFilter, statusFilter]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const act = async (id, action) => {
    if (action === 'revoke' && !window.confirm('Remove this programme enrolment? The learner loses access immediately (progress is kept).')) return;
    try {
      await axios.post(`${API}/admin/programme-enrollments/${id}/${action}`, {}, { headers });
      toast.success(action === 'revoke' ? 'Enrolment removed' : 'Enrolment restored');
      fetchList();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  const progName = (p) => p.programme_name || p.title || p.name || p.programme_id;

  return (
    <div data-testid="programme-enrollments-admin">
      <div className="flex gap-2 mb-4">
        {[['list', 'All programme enrolments', Users], ['enroll', 'Enrol into programme', CheckCircle]].map(([id, label, Icon]) => (
          <button key={id} onClick={() => setView(id)} data-testid={`prog-enrol-tab-${id}`}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${view === id ? 'bg-zinc-200 text-black' : 'text-zinc-400 hover:text-white border border-zinc-800'}`}>
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>

      {view === 'enroll' ? (
        <EnrolPanel headers={headers} programmes={programmes} progName={progName}
          preselect={progFilter} onDone={() => { setView('list'); fetchList(); }} />
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-3 mb-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <input className={`w-full pl-9 ${inputCls}`} placeholder="Search learner name or email..." value={search}
                onChange={(e) => setSearch(e.target.value)} data-testid="prog-enrol-search" />
            </div>
            <select className={inputCls} value={progFilter} onChange={(e) => setProgFilter(e.target.value)} data-testid="prog-enrol-filter">
              <option value="">All programmes</option>
              {programmes.map((p) => <option key={p.programme_id} value={p.programme_id}>{progName(p)}</option>)}
            </select>
            <select className={inputCls} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="prog-enrol-status-filter">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="revoked">Removed</option>
            </select>
          </div>
          <ProgrammeRows rows={rows} loading={loading} programmes={programmes} progName={progName} onAct={act} />
        </>
      )}
    </div>
  );
}

function ProgrammeRows({ rows, loading, programmes, progName, onAct }) {
  const nameOf = (r) => { const p = programmes.find((x) => x.programme_id === r.programme_id); return p ? progName(p) : (r.programme_title || r.programme_id); };
  return (
    <div className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-x-auto">
      <p className="text-xs text-zinc-500 px-4 pt-3" data-testid="prog-enrol-count">{rows.length} enrolment{rows.length === 1 ? '' : 's'}</p>
      <table className="w-full min-w-[760px]">
        <thead className="bg-zinc-800/50"><tr>
          {['Learner', 'Email', 'Programme', 'Enrolled', 'Progress', 'Status', 'Actions'].map((h) => (
            <th key={h} className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase">{h}</th>
          ))}
        </tr></thead>
        <tbody className="divide-y divide-zinc-800">
          {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center text-zinc-500">No programme enrolments found</td></tr>
            : rows.map((r) => (
              <tr key={r.enrollment_id} className="hover:bg-zinc-800/30" data-testid={`prog-enrol-row-${r.enrollment_id}`}>
                <td className="px-4 py-3 text-sm text-white">{r.user_name || '—'}</td>
                <td className="px-4 py-3 text-sm text-zinc-400">{r.email}</td>
                <td className="px-4 py-3 text-sm text-zinc-300">{nameOf(r)}</td>
                <td className="px-4 py-3 text-xs text-zinc-500">{(r.enrolled_at || '').slice(0, 10)}</td>
                <td className="px-4 py-3 text-sm text-zinc-300">{Math.round(r.overall_progress || 0)}%</td>
                <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS[r.status]}`}>{r.status === 'revoked' ? 'removed' : r.status}</span></td>
                <td className="px-4 py-3">
                  {r.status === 'revoked' ? (
                    <button onClick={() => onAct(r.enrollment_id, 'reenroll')} className="flex items-center gap-1 text-xs text-green-400 hover:text-green-300" data-testid={`prog-reenroll-${r.enrollment_id}`}>
                      <RotateCcw className="h-3.5 w-3.5" /> Re-enrol
                    </button>
                  ) : (
                    <button onClick={() => onAct(r.enrollment_id, 'revoke')} className="flex items-center gap-1 text-xs text-red-400 hover:text-red-300" data-testid={`prog-revoke-${r.enrollment_id}`}>
                      <Ban className="h-3.5 w-3.5" /> Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function EnrolPanel({ headers, programmes, progName, preselect, onDone }) {
  const [selUsers, setSelUsers] = useState([]);
  const [selProgs, setSelProgs] = useState(preselect ? [preselect] : []);
  const [uq, setUq] = useState('');
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState('loading');
  const toggle = (arr, set, id) => set(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  useEffect(() => {
    setLoadState('loading');
    const t = setTimeout(() => {
      axios.get(`${API}/admin/users`, { headers, params: { search: uq || undefined, limit: 200 } })
        .then((r) => { setFound(r.data.users || []); setTotal(r.data.total || 0); setLoadState('ok'); })
        .catch(() => setLoadState('error'));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uq]);

  const submit = async () => {
    if (!selUsers.length || !selProgs.length) { toast.error('Select at least one user and one programme'); return; }
    setBusy(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/programme-enrollments/enroll`, { user_ids: selUsers, programme_ids: selProgs }, { headers });
      toast.success(`Enrolled ${d.enrolled}, re-enrolled ${d.reenrolled}, already enrolled ${d.already_enrolled}`);
      if (d.errors?.length) toast.error(`${d.errors.length} error(s)`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to enrol'); }
    finally { setBusy(false); }
  };

  const fUsers = found;
  const box = 'bg-navigate-surface border border-zinc-800 rounded-xl p-4';
  const item = 'flex items-center gap-2 p-2 rounded hover:bg-zinc-800/40 cursor-pointer text-sm text-zinc-300';
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className={box}>
        <h3 className="text-sm font-semibold text-white mb-3">Select Users ({selUsers.length})</h3>
        <input className="w-full h-9 bg-navigate-dark border border-zinc-700 rounded-md text-sm text-white px-2 mb-3" placeholder="Search by name or email..." value={uq} onChange={(e) => setUq(e.target.value)} data-testid="prog-enrol-user-search" />
        <p className="text-[11px] text-zinc-500 mb-2" data-testid="prog-enrol-user-count">
          {loadState === 'loading' ? 'Loading users...' : loadState === 'error' ? '' : `Showing ${fUsers.length} of ${total} user(s)${total > fUsers.length ? ' — search to narrow down' : ''}`}
        </p>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {fUsers.map((u) => (
            <label key={u.user_id} className={item}>
              <input type="checkbox" checked={selUsers.includes(u.user_id)} onChange={() => toggle(selUsers, setSelUsers, u.user_id)} data-testid={`prog-enrol-user-${u.email}`} />
              <span className="min-w-0 flex-1 truncate">{u.full_name || u.email} <span className="text-zinc-500">({u.email})</span></span>
              <span className="text-[10px] uppercase text-zinc-500 shrink-0">{(u.role || '').replace('_', ' ')}{u.is_active === false ? ' · inactive' : ''}</span>
            </label>
          ))}
          {loadState === 'error' && <p className="text-xs text-rose-300 p-2" data-testid="prog-enrol-user-error">Couldn't load users. Check your connection and try searching again.</p>}
          {loadState === 'ok' && fUsers.length === 0 && <p className="text-xs text-zinc-500 p-2">No users match "{uq}"</p>}
        </div>
      </div>
      <div className={box}>
        <h3 className="text-sm font-semibold text-white mb-3">Select Programmes ({selProgs.length})</h3>
        <div className="max-h-96 overflow-y-auto space-y-1">
          {programmes.map((p) => (
            <label key={p.programme_id} className={item}>
              <input type="checkbox" checked={selProgs.includes(p.programme_id)} onChange={() => toggle(selProgs, setSelProgs, p.programme_id)} data-testid={`prog-enrol-programme-${p.programme_id}`} />
              <span>{progName(p)}</span>
            </label>
          ))}
          {programmes.length === 0 && <p className="text-xs text-zinc-500 p-2">No programmes</p>}
        </div>
      </div>
      <div className="md:col-span-2">
        <button onClick={submit} disabled={busy || !selUsers.length || !selProgs.length} className="btn-gold px-6 py-2.5 text-sm disabled:opacity-50" data-testid="prog-enrol-confirm-btn">
          {busy ? 'Enrolling...' : `Enrol ${selUsers.length} user(s) in ${selProgs.length} programme(s)`}
        </button>
      </div>
    </div>
  );
}
